// Public onboarding for a white-label custom domain — the "request an
// invitation" half of the branded gate.
//
// ── Why this is a REQUEST, not a sign-up ─────────────────────────────────
// The Bellforge landing states it outright: "An invitation, not a password
// form." Nobody creates credentials here. This posts to the platform's
// generic public-onboarding surface (`/core/onboarding/*`), which mints a
// workspace INVITATION; accepting that invitation is what creates the
// account. Building a password sign-up instead would have been a second
// account-creation path for the same org, diverging from the landing the
// user asked us to match.
//
// ── Why the role list is fetched, never hardcoded ────────────────────────
// `role` is an ALLOWLIST INDEX into the org's own
// `workspace.settings.publicOnboarding.roles` — the server matches the key
// case-insensitively and applies the teams from ITS config, never from our
// payload. So the client cannot grant itself anything, and "every type
// except admin" is enforced server-side by the org's config (the four
// published roles are searcher/investor/institution/advisor — admin is
// simply not in the allowlist) rather than by a filter we could get wrong.
// Fetching the list also means a copy change on the org needs no redeploy.
//
// Both functions are `this`-bound methods (`el.call(...)`) and neither ever
// throws: a dead endpoint must leave the branded gate usable, not strand it.

// The tenant's display name, read from the SAME pre-auth brand record the
// panes render from (`root.whitelabelBrand`, resolved by
// workspace/functions/whitelabelBrand.js). This shared file must never carry
// a tenant literal: the next white-label org gets its own name with no
// deploy (DESIGN-WHITELABEL-SHARED-FILES-REVIEW-1).
const _brandNameOf = (el) => {
  const b = el && el.state && el.state.root && el.state.root.whitelabelBrand
  return b && b.name ? String(b.name).trim() : ''
}

/**
 * Read the org's public onboarding config for this workspace.
 *
 * Returns `null` for every "not available" case — 404 (no config, or the
 * org disabled it), a network failure, or a payload with no usable roles.
 * A null return is the signal for the UI to hide the whole signup
 * affordance, which is why it is not distinguished from an error: an
 * unconfigured org and an unreachable API look identical to a visitor.
 */
export const fetchWhitelabelOnboarding = async function (opts = {}) {
  const el = this
  const win = opts.win || (typeof window !== 'undefined' ? window : null)
  const workspaceKey = String(opts.workspaceKey || '').trim()
  if (!workspaceKey) return null

  const apiBase = String(
    opts.apiBase || (el && el.call ? el.call('workspaceApiBase') : '') || ''
  ).replace(/\/+$/, '')
  const fetchImpl =
    opts.fetchImpl || (win && typeof win.fetch === 'function' ? win.fetch.bind(win) : null)
  if (!apiBase || typeof fetchImpl !== 'function') return null

  try {
    const res = await fetchImpl(
      `${apiBase}/core/onboarding/options?workspace=${encodeURIComponent(workspaceKey)}`,
      // Credential-less like the brand and persona reads: a logged-out
      // visitor must be able to resolve this, and it must not depend on
      // whatever session the browser already carries.
      { credentials: 'omit' }
    )
    if (!res || !res.ok) return null
    const body = await res.json().catch(() => null)
    if (!body || body.enabled !== true) return null

    const roles = (Array.isArray(body.roles) ? body.roles : [])
      .filter((r) => r && typeof r.key === 'string' && r.key.trim() && r.label)
      .map((r) => ({
        key: String(r.key).trim(),
        label: String(r.label),
        description: r.description ? String(r.description) : ''
      }))
    if (!roles.length) return null

    // `captcha` is present only when the org requires one. The site key is
    // public by construction (it ships in any client that renders the
    // widget); the secret never leaves the server.
    const captcha =
      body.captcha && body.captcha.siteKey
        ? { provider: String(body.captcha.provider || ''), siteKey: String(body.captcha.siteKey) }
        : null

    return { roles, captcha }
  } catch (_) {
    return null
  }
}

/**
 * Submit the invitation request.
 *
 * Resolves `{ ok: true, message }` or `{ ok: false, message }` — never
 * throws. Messages mirror the landing's wording so the two surfaces of the
 * same programme say the same thing.
 */
export const submitWhitelabelOnboarding = async function (payload = {}, opts = {}) {
  const el = this
  const win = opts.win || (typeof window !== 'undefined' ? window : null)

  const name = String(payload.name || '').trim()
  const email = String(payload.email || '').trim()
  const role = String(payload.role || '').trim()
  const workspaceKey = String(payload.workspaceKey || '').trim()

  // Validate in the order the form reads top-to-bottom, so the message
  // points at the first field the visitor still has to fix.
  if (!role) return { ok: false, message: el.call('polyglot', 'auth.whitelabel.needRole') }
  if (!name) return { ok: false, message: el.call('polyglot', 'auth.whitelabel.needName') }
  // Shape only — the server is the authority. This exists to save a
  // round-trip on an obvious typo, not to be a second validator.
  if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return { ok: false, message: el.call('polyglot', 'auth.whitelabel.needEmail') }
  }

  const apiBase = String(
    opts.apiBase || (el && el.call ? el.call('workspaceApiBase') : '') || ''
  ).replace(/\/+$/, '')
  const fetchImpl =
    opts.fetchImpl || (win && typeof win.fetch === 'function' ? win.fetch.bind(win) : null)
  if (!apiBase || !workspaceKey || typeof fetchImpl !== 'function') {
    return { ok: false, message: el.call('polyglot', 'auth.whitelabel.signupUnavailable') }
  }

  try {
    const res = await fetchImpl(`${apiBase}/core/onboarding/request`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',
      body: JSON.stringify({
        workspace: workspaceKey,
        name,
        email,
        role,
        captchaToken: payload.captchaToken || undefined
      })
    })
    const body = await res.json().catch(() => null)

    if (res.ok && body && body.ok) {
      // The server reports whether the mail actually went out. Saying
      // "check your inbox" when `emailSent` is false would send someone to
      // watch a mailbox nothing was sent to.
      if (body.emailSent === false) {
        return {
          ok: true,
          message: el.call('polyglot', 'auth.whitelabel.requestReceived')
        }
      }
      if (body.alreadyInvited) {
        return {
          ok: true,
          message: el.call('polyglot', 'auth.whitelabel.alreadyInvited')
        }
      }
      // The org's name comes from the live brand record, never a literal —
      // the fallback keeps the sentence whole for a caller with no brand in
      // state (tests, degenerate mounts).
      const brandName = _brandNameOf(el)
      return {
        ok: true,
        message: brandName
          ? el.call('polyglot', 'auth.whitelabel.inboxBranded').replace('{brand}', brandName)
          : el.call('polyglot', 'auth.whitelabel.inbox')
      }
    }

    // Map only what a visitor can act on. Everything else collapses to one
    // message: the endpoint deliberately refuses without enumerating (a
    // 404 means "no config OR disabled OR unknown workspace" and must not
    // be decoded), so guessing on their behalf would be a false statement.
    const code = (body && body.error) || ''
    const status = res.status
    if (code === 'invalid_email')
      return { ok: false, message: el.call('polyglot', 'auth.whitelabel.needEmail') }
    if (code === 'invalid_role')
      return { ok: false, message: el.call('polyglot', 'auth.whitelabel.needValidOption') }
    if (code === 'captcha_failed') {
      return { ok: false, message: el.call('polyglot', 'auth.whitelabel.needCaptcha') }
    }
    if (code === 'captcha_unavailable') {
      return {
        ok: false,
        message: el.call('polyglot', 'auth.whitelabel.captchaDown')
      }
    }
    if (status === 429)
      return { ok: false, message: el.call('polyglot', 'auth.whitelabel.tooMany') }
    if (status === 404) {
      return {
        ok: false,
        message: el.call('polyglot', 'auth.whitelabel.signupSoon')
      }
    }
    return { ok: false, message: el.call('polyglot', 'auth.whitelabel.genericError') }
  } catch (_) {
    return { ok: false, message: el.call('polyglot', 'auth.whitelabel.networkError') }
  }
}
